import React, { useState } from 'react'
import api from '../../services/api'
import { Search, Users, FolderKanban, FileText, Receipt } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Admin ID Lookup — trace consultant → project → PO → contract → invoice → payment
 * from a single search box. Accepts our canonical IDs (3P-CON-…, 3P-PRJ-…, 3P-CTR-…)
 * or a client/PM external reference, full or partial.
 */
const IdLookup = () => {
  const { t } = useTranslation('people')
  const [q, setQ] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searched, setSearched] = useState(false)

  const run = async (e) => {
    e?.preventDefault()
    if (!q.trim()) return
    setLoading(true); setError(null); setSearched(true)
    try {
      const r = await api.get('/admin/lookup', { params: { q: q.trim() } })
      setResult(r.data)
    } catch (err) {
      setError(err.response?.data?.detail || 'Lookup failed')
      setResult(null)
    } finally {
      setLoading(false)
    }
  }

  const counts = result
    ? [result.consultants.length, result.projects.length, result.contracts.length, result.invoices.length]
    : [0, 0, 0, 0]
  const empty = searched && !loading && result && counts.every(c => c === 0)

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Search className="w-5 h-5 text-indigo-600" /> ID Overview
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Look up by Consultant ID, Project ID, Contract ID or client reference — see everything linked.
        </p>
      </div>

      <div className="p-6 space-y-5">
        <form onSubmit={run} className="flex gap-2">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder={t('ph_id_lookup')}
            className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="submit"
            disabled={loading || !q.trim()}
            className="px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Searching…' : 'Search'}
          </button>
        </form>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
        {empty && <p className="text-sm text-gray-500 text-center py-4">No records match “{result.query}”.</p>}

        {result && !empty && (
          <div className="space-y-5">
            {/* Consultants */}
            {result.consultants.length > 0 && (
              <Section icon={<Users className="w-4 h-4" />} title={t('t_consultants')}>
                {result.consultants.map(c => (
                  <Row key={c.id}
                    code={c.consultant_code}
                    primary={c.name}
                    meta={[c.specialization, c.status].filter(Boolean).join(' · ')}
                  />
                ))}
              </Section>
            )}
            {/* Projects */}
            {result.projects.length > 0 && (
              <Section icon={<FolderKanban className="w-4 h-4" />} title={t('t_projects')}>
                {result.projects.map(p => (
                  <Row key={p.id}
                    code={p.project_number}
                    primary={p.name}
                    meta={[
                      p.external_ref ? `Ref: ${p.external_ref}` : null,
                      p.status,
                      p.project_manager ? `PM: ${p.project_manager}` : null,
                      `$${(p.total_budget || 0).toLocaleString()}`,
                    ].filter(Boolean).join(' · ')}
                  />
                ))}
              </Section>
            )}
            {/* Contracts */}
            {result.contracts.length > 0 && (
              <Section icon={<FileText className="w-4 h-4" />} title={t('t_contracts')}>
                {result.contracts.map(c => (
                  <Row key={c.id}
                    code={c.contract_number}
                    primary={c.consultant || '—'}
                    meta={[
                      c.consultant_code,
                      c.project_number,
                      c.status,
                    ].filter(Boolean).join(' · ')}
                  />
                ))}
              </Section>
            )}
            {/* Invoices */}
            {result.invoices.length > 0 && (
              <Section icon={<Receipt className="w-4 h-4" />} title={t('t_invoices')}>
                {result.invoices.map(i => (
                  <Row key={i.id}
                    code={i.invoice_number}
                    primary={`${i.currency || 'USD'} ${(i.net_amount || i.amount || 0).toLocaleString()}`}
                    meta={[
                      i.consultant,
                      i.project_number,
                      i.status,
                      i.paid_at ? `Paid ${i.paid_at.slice(0, 10)}` : null,
                    ].filter(Boolean).join(' · ')}
                  />
                ))}
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const Section = ({ icon, title, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1.5 mb-2">
      {icon} {title}
    </p>
    <div className="space-y-2">{children}</div>
  </div>
)

const Row = ({ code, primary, meta }) => (
  <div className="flex items-center justify-between gap-3 border border-gray-100 rounded-lg px-3 py-2 bg-gray-50">
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900 truncate">{primary}</p>
      {meta && <p className="text-xs text-gray-500 truncate">{meta}</p>}
    </div>
    {code && <span className="text-[11px] font-mono text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded shrink-0">{code}</span>}
  </div>
)

export default IdLookup
